import {downloadFileWithContents} from "../../utils/api";
import {
    Box,
    Button, Checkbox,
    Container,
    Flashbar,
    Grid,
    Header,
    List, LiveRegion,
    Pagination,
    SpaceBetween, Spinner, Tabs
} from "@cloudscape-design/components";
import {DrawioReadonly} from "../../reusable_components/drawio_diagram";
import {useEffect, useState} from "react";
import Avatar from "@cloudscape-design/chat-components/avatar";
import {useNavigate} from "react-router-dom";
import {ArchitectureUtils} from "../../utils/architecture";
import {LoadingBar} from "@cloudscape-design/chat-components";


const FlowSteps = ({architecture}) => {
    const [page, setPage] = useState(1);
    const flow = architecture?.interactionFlow ?? []
    const itemsPerPage = 5
    const startIndex = itemsPerPage * (page - 1)
    const endIndex = startIndex + itemsPerPage

    let items = []

    for (let i = itemsPerPage * (page - 1); i < flow.length && i < endIndex; i++) {
        items.push({id: flow[i].step_index, content: flow[i].description})
    }

    return (
        <SpaceBetween direction={'vertical'} size={'xs'}>
            <Pagination
                currentPageIndex={page}
                onChange={({ detail }) =>
                    setPage(detail.currentPageIndex)
                }
                pagesCount={Math.ceil(flow.length / itemsPerPage)}
            />

            <List
                items={items}
                renderItem={item => ({
                    id: item.id,
                    content: (
                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <Avatar initials={item.id} ariaLabel={item.id}></Avatar>
                            <Box variant={'span'}>{item.content}</Box>
                        </div>
                    )
                })}
            />
        </SpaceBetween>
    )
}


const Layers = ({architectureLayers, visibleLayers, setVisibleLayers}) => {
    const [page, setPage] = useState(1);

    const flow = architectureLayers ?? []
    const itemsPerPage = 4
    const startIndex = itemsPerPage * (page - 1)
    const endIndex = startIndex + itemsPerPage

    let items = []

    for (let i = itemsPerPage * (page - 1); i < flow.length && i < endIndex; i++) {
        items.push(flow[i])
    }

    function updateVisibleLayers(layers, show) {
        setVisibleLayers(prev =>
            show ? [...new Set([...prev, ...layers])] : prev.filter(item => !layers.includes(item))
        );
    }

    return (
        <SpaceBetween direction={'vertical'} size={'xs'}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Pagination
                    currentPageIndex={page}
                    onChange={({ detail }) =>
                        setPage(detail.currentPageIndex)
                    }
                    pagesCount={Math.ceil(flow.length / itemsPerPage)}
                />

                <SpaceBetween direction={'horizontal'} size={'xxs'} alignItems={'center'}>
                    <Button variant={'inline-link'} onClick={() => {
                        updateVisibleLayers(architectureLayers.map(l => l.layer_id), true)
                    }}>Show all</Button>

                    <Box variant={'code'}>/</Box>

                    <Button variant={'inline-link'} onClick={() => {
                        updateVisibleLayers(architectureLayers.map(l => l.layer_id), false)
                    }}>Hide all</Button>
                </SpaceBetween>
            </div>

            <List
                items={items}
                renderItem={item => ({
                    id: item.layer_id,
                    content: (
                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <Box margin={{right: 'xxs'}}>
                                <Checkbox
                                    checked={visibleLayers.includes(item.layer_id)}
                                    onChange={({ detail }) =>
                                        updateVisibleLayers([item.layer_id], detail.checked)
                                    }
                                ></Checkbox>
                            </Box>

                            <SpaceBetween direction={'vertical'} size={'xxs'}>
                                <Box variant={'awsui-key-label'}>{item.title}</Box>
                                <Box variant={'span'}>{item.description}</Box>
                            </SpaceBetween>
                        </div>
                    )
                })}
            />
        </SpaceBetween>
    )
}


const ArchitectureDiagram = ({architecture, diagramContent, canvasMode}) => {
    let description = 'This architecture diagram was generated based on the description shown above. Update the description and click “Recreate architecture” to generate a new version.'
    if (canvasMode) description += ' To store it for future reference and generate a starter IaC project, click "Save architecture".'

    function downloadAsDrawio() {
        downloadFileWithContents(
            architecture.title.replaceAll(' ', '_') + '.drawio',
            architecture.diagramContent
        )
    }

    return diagramContent != null ? (
        <Container
            header={
                <Header
                    variant={'h2'}
                    description={description}
                    actions={
                        <SpaceBetween direction={'horizontal'} size={'s'}>
                            <Button iconName={'download'} disabled={ArchitectureUtils.isGeneratingArchitecture(architecture)} onClick={downloadAsDrawio}>Download as draw.io file</Button>
                        </SpaceBetween>
                    }
                    counter={
                        ArchitectureUtils.isGeneratingArchitecture(architecture) && (
                            <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                                <Spinner variant={'normal'} size={'normal'} />
                            </div>
                        )
                    }
                >
                    Architecture diagram
                </Header>
            }
        >
            <DrawioReadonly xml={diagramContent}></DrawioReadonly>
        </Container>

    ) : <></>
}


export const Architecture = ({architecture, canvasMode}) => {
    const [diagramContent, setDiagramContent] = useState(architecture?.diagramContent);
    const [visibleLayers, setVisibleLayers] = useState(architecture?.architectureLayers.map(x => x.layer_id))

    const parser = new DOMParser();
    const serializer = new XMLSerializer();

    useEffect(() => {
        setDiagramContent(architecture?.diagramContent)
    }, [architecture?.diagramContent])

    useEffect(() => {
        const xmlDoc = parser.parseFromString(diagramContent, 'text/xml');
        const mxCells = xmlDoc.getElementsByTagName('mxCell');

        for (const el of mxCells) {
            const show = visibleLayers.includes(el.getAttribute('custom-layerId'));

            let style = el.getAttribute('style') || '';
            style = style.replace(/opacity=[^;]*;?/g, '');
            style = style.replace(/textOpacity=[^;]*;?/g, '');

            if (!show) style += 'opacity=0;textOpacity=0;';

            el.setAttribute('style', style);
        }

        setDiagramContent(serializer.serializeToString(xmlDoc));
    }, [visibleLayers, diagramContent]);

    useEffect(() => {
        setVisibleLayers(architecture?.architectureLayers.map(x => x.layer_id))
    }, [architecture?.architectureLayers])

    return (
        <Grid
            gridDefinition={[{colspan: 9}, {colspan: 3}]}
        >
            <ArchitectureDiagram
                diagramContent={diagramContent}
                architecture={architecture}
                canvasMode={canvasMode}>
            </ArchitectureDiagram>

            <Container
                header={
                    <Header variant={'h2'}>
                        Workflow
                    </Header>
                }
            >
                <Tabs
                    tabs={[
                        {
                            label: "Layered view",
                            id: "second",
                            content: <Layers
                                architectureLayers={architecture?.architectureLayers ?? []}
                                visibleLayers={visibleLayers}
                                setVisibleLayers={setVisibleLayers}
                            />
                        },
                        {
                            label: "Interaction steps",
                            id: "first",
                            content: <FlowSteps architecture={architecture}/>
                        }
                    ]}
                />
            </Container>
        </Grid>
    )
}


export const GeneratingArchitectureFlashbar = () => (
    <Flashbar items={
        [
            {
                type: "info",
                loading: true,
                header: 'Recreating architecture',
                content: "Please wait while the architecture is being recreated based on the description you provided. The content will refresh automatically once the update is complete.",
                dismissible: false,
                id: "generating_architecture"
            }
        ]
    } />
)


export const ArchitectureGenerationErrorFlashbar = ({message}) => (
    <Flashbar items={
        [
            {
                type: "error",
                header: 'Failed to recreate architecture',
                content: message,
                dismissible: false,
                id: "generating_architecture_error"
            }
        ]
    } />
)


export const ArchitectureSavedFlashbar = ({archId}) => {
    const navigate = useNavigate();

    return archId == null? <></> : <Flashbar items={
        [
            {
                type: "success",
                content: "Architecture saved. You can now navigate to the details page to iterate on it or generate the IaC project that implements it.",
                action: <Button onClick={() => navigate('/architectures/' + archId, {replace: true})}>View details</Button>,
                id: `message_${archId}`
            }
        ]
    }/>
}
